import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing environment variables for Supabase connection');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  throw new Error('Invalid Supabase URL format');
}

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Enhanced retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 10000; // 10 seconds
const TIMEOUT = 30000; // 30 seconds

export const supabase = createClient<any>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: isBrowser ? localStorage : undefined,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce',
  },
  global: {
    headers: { 
      'x-application-name': 'dashboard-metalurgico',
      'Content-Type': 'application/json',
    },
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
  // Enhanced fetch configuration with exponential backoff retry
  fetch: (url, options = {}) => {
    const fetchWithRetry = async (attempt = 0): Promise<Response> => {
      // Check for network connectivity in browser environment
      if (isBrowser && !navigator.onLine) {
        throw new Error('No internet connection available');
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
          },
          mode: 'cors',
          credentials: 'include',
        });

        clearTimeout(timeoutId);

        // Handle HTTP errors
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            `HTTP error ${response.status}: ${errorData.message || response.statusText}`
          );
        }

        return response;
      } catch (error) {
        // Handle specific error types
        if (error.name === 'AbortError') {
          throw new Error(`Request timed out after ${TIMEOUT/1000} seconds`);
        }

        // Implement exponential backoff for retries
        if (attempt < MAX_RETRIES) {
          const delay = Math.min(
            INITIAL_RETRY_DELAY * Math.pow(2, attempt),
            MAX_RETRY_DELAY
          );
          
          console.warn(`Fetch attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(attempt + 1);
        }

        // If we've exhausted all retries or encountered a non-retriable error
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
        }

        throw error;
      }
    };

    return fetchWithRetry();
  },
});

// Add a health check method to verify connection
export const checkSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase.from('user_roles').select('count').limit(1);
    return !error;
  } catch {
    return false;
  }
};

export type Tables = Database['public']['Tables'];
export type Enums = Database['public']['Enums'];